import { AxiosError } from 'axios';
import APIErrorMessage from 'config/APIErrorMessage';
import SUCCESS_MESSAGE from 'constants/successMessage';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import setting from 'lib/api/setting';
import { OtpChannel } from 'typings/auth';
import { formatRemoveErrorTypeMobile } from 'utils/formatErrorMessage';
import create from 'zustand';
import { ResponseFormats } from '../../../../@types/api-response';

interface UpdatePhoneParams {
  phone: string;
  code: string;
  channel: OtpChannel;
}

interface ChangePhoneSlice {
  changeToken: string | null;
  isLoading: boolean;
  phone: {
    phone: string;
    code: string;
  };
  channel: OtpChannel;
  setPhone: (phone: string, code: string) => void;
  getChangeToken: (password: string) => Promise<ResponseFormats>;
  updatePhone: (params: UpdatePhoneParams) => Promise<ResponseFormats>;
  confirmOTP: (otp: string) => Promise<ResponseFormats>;
  resendOTP: () => Promise<ResponseFormats>;
}

const changePhoneStore = create<ChangePhoneSlice>((set, get) => ({
  changeToken: null,
  isLoading: false,
  phone: null,
  channel: null,
  setPhone: (phone, code) => {
    set({ phone: { phone, code } });
  },
  getChangeToken: async (password) => {
    set({ isLoading: true });
    try {
      const data = {
        type: 'CHANGE_TOKEN_TYPE_PHONE',
        req_type: 'CHANGE_TOKEN_REQ_TYPE_EDIT',
        password,
      };

      const res = await setting.getChangePasswordToken(data);

      if (!res.data) {
        throw new Error('Attempt to get change token failed');
      }

      const { data: response, error, error_type, message } = res.data;

      if (error_type || error) {
        throw new Error(message);
      }

      handleErrorMessageAPI(message, SUCCESS_MESSAGE.ALERT_GREEN);
      set({ changeToken: response.change_token, isLoading: false });
      return { data, message };
    } catch (e) {
      set({ isLoading: false });
    }
  },
  updatePhone: async (params) => {
    set({ isLoading: true });
    try {
      const { changeToken } = get();
      const paramsData = {
        ...params,
        change_token: changeToken,
      };

      const res = await setting.updatePhone(paramsData);

      if (!res.data) {
        throw new Error('Attempt to update phone failed');
      }

      const { data, error, error_type, message } = res.data;

      if (error_type || error) {
        throw new Error(message);
      }

      handleErrorMessageAPI(message, SUCCESS_MESSAGE.ALERT_GREEN);
      set({
        phone: {
          phone: params.phone,
          code: params.code,
        },
        channel: params.channel,
        isLoading: false,
      });

      return { data, message };
    } catch (e) {
      set({ isLoading: false });

      const { status, data } = (e as AxiosError)?.response || {};
      const { error_type: errorType, message } = data || {};

      if (errorType !== 'OTP_LIMIT') {
        APIErrorMessage.APIExodusErrorHandler(
          status,
          errorType,
          formatRemoveErrorTypeMobile(message),
        );
      }

      return Promise.reject(e);
    }
  },
  confirmOTP: async (otp) => {
    set({ isLoading: false });
    try {
      const { changeToken } = get();
      const params = {
        change_token: changeToken,
        otp,
      };

      const res = await setting.confirmPhoneOTP(params);

      if (!res.data) {
        throw new Error('Attempt to confirm otp failed');
      }

      const { data, error, error_type, message } = res.data;

      if (error_type || error) {
        throw new Error(message);
      }

      handleErrorMessageAPI(message, SUCCESS_MESSAGE.ALERT_GREEN);
      set({ isLoading: false });

      return { data, message };
    } catch (e) {
      set({ isLoading: false });
    }
  },
  resendOTP: async () => {
    const { phone, channel, updatePhone } = get();

    return updatePhone({ ...phone, channel });
  },
}));

export default changePhoneStore;
